// Client twin of the server's MediaReferences pattern. Kept identical to
// src/__tests__/fixtures/media-references.json, which the C# tests also read.
// Inside a quoted attribute value, or when the input is the bare URL, the name runs to the closing
// quote so system file names containing spaces stay whole; elsewhere whitespace ends it.
export const MEDIA_REF_SOURCE = String.raw`(?<prefix>(?:https?:)?//[^/"'\s<>()]+)?/api/media/(?<name>(?<=(?:^|["'])(?:(?:https?:)?//[^/"'\s<>()]+)?/api/media/)[^"'?#<>()\r\n]+|[^"'?#\s<>()]+)(?<query>[?#][^"'\s<>()]*)?`

export const MEDIA_REF_PATTERN = new RegExp(MEDIA_REF_SOURCE, 'gi')

interface MediaRefGroups {
  prefix?: string
  name: string
  query?: string
}

// A relative reference is only rewritten when it starts a URL value (attribute,
// CSS url() or the whole string), never when /api/media/ sits inside another path.
const URL_START_CHARS = new Set(['"', "'", '(', '=', ' ', '\t', '\n', '\r'])

function decodeName(raw: string): string {
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function startsUrlValue(source: string, index: number): boolean {
  return index === 0 || URL_START_CHARS.has(source[index - 1])
}

export function extractMediaNames(html: string | null | undefined): string[] {
  if (!html) return []
  const names = new Set<string>()
  for (const match of html.matchAll(new RegExp(MEDIA_REF_SOURCE, 'gi'))) {
    names.add(decodeName((match.groups as unknown as MediaRefGroups).name))
  }
  return [...names]
}

export function toAbsoluteMediaUrls(html: string | null | undefined, baseUrl: string): string {
  if (!html) return ''
  const base = baseUrl.replace(/\/+$/, '')
  if (!base) return html
  return html.replace(new RegExp(MEDIA_REF_SOURCE, 'gi'), (match: string, ...args: unknown[]) => {
    const groups = args[args.length - 1] as MediaRefGroups
    const offset = args[args.length - 3] as number
    if (groups.prefix || !startsUrlValue(html, offset)) return match
    return `${base}${match}`
  })
}

export function replaceMediaUrls(
  html: string | null | undefined,
  resolve: (name: string) => string | null,
): string {
  if (!html) return ''
  return html.replace(new RegExp(MEDIA_REF_SOURCE, 'gi'), (match: string, ...args: unknown[]) => {
    const groups = args[args.length - 1] as MediaRefGroups
    const offset = args[args.length - 3] as number
    if (!groups.prefix && !startsUrlValue(html, offset)) return match
    return resolve(decodeName(groups.name)) ?? match
  })
}

export function mediaNameFromUrl(url: string | null | undefined): string | null {
  if (!url) return null
  const match = new RegExp(MEDIA_REF_SOURCE, 'i').exec(url)
  return match ? decodeName((match.groups as unknown as MediaRefGroups).name) : null
}
